
import gsap from 'gsap';
import { DrawSVGPlugin } from 'gsap/DrawSVGPlugin';
import { CustomEase } from 'gsap/dist/CustomEase';
import StoryblokClient from 'storyblok-js-client';
import Vue from 'vue';
import { transformLink, transformRichText } from '~/transforms/transformComponents';

gsap.registerPlugin(DrawSVGPlugin, CustomEase);
CustomEase.create('curveNatural', '.4,0,.2,1');
CustomEase.create('curveAppear', '.03,0,.2,1');

export default Vue.extend({
  props: {
    current: {
      type: String,
    },
    category: {
      type: String,
    },
    state: {
      type: Boolean,
    },
  },
  data() {
    return {
      Storyblok: new StoryblokClient({
        accessToken: process.env.STORYBLOK_API_KEY,
      }),
      navInline: false as Boolean,
      getService: '' as any,
      navData: Array(),
      serviceData: null as any,
      animationData: null as any,
      categoryData: null as any,
      service: false as boolean,
      showLayer: false as boolean,
      moveToLogo: false as boolean,
    };
  },
  methods: {
    openLayer() {
      this.showLayer = true;
      const currentQuerry = { ...this.$route.query };
      if (currentQuerry.formSent) delete currentQuerry.formSent;
      this.$router.push({ query: { ...currentQuerry, ...{ showForm: '1' } } });
      //
      gsap.config({ nullTargetWarn: false });
      //
      setTimeout(() => {
        gsap.set(['.c-nav-inline__layer'], { yPercent: 100, opacity: 1 });
        gsap.to(['.c-nav-inline__layer'], { yPercent: 0, ease: 'curveAppear', duration: 0.6, delay: 0.1 });
        gsap.to(['.c-nav-inline__layer-bg'], { opacity: 0.5, ease: 'curveAppear', duration: 0.3 });
      }, 100);

      window.addEventListener('modalClose', this.closeLayer, { passive: true });
    },
    closeLayer() {
      if (this.$route.query.showForm) {
        const currentQuery = { ...this.$route.query };
        if (currentQuery.formSent) delete currentQuery.formSent;
        if (currentQuery.success) delete currentQuery.success;
        if (currentQuery.showForm) delete currentQuery.showForm;
        this.$router.push({ query: currentQuery });
      }

      gsap.config({ nullTargetWarn: false });
      //
      gsap.to(['.c-nav-inline__layer'], { yPercent: 100, ease: 'curveAppear', duration: 0.4 });
      gsap.to(['.c-nav-inline__layer-bg'], { opacity: 0, ease: 'curveAppear', duration: 0.3, delay: 0.1 });
      //
      setTimeout(() => {
        this.showLayer = false;
      }, 400);

      window.removeEventListener('modalClose', this.closeLayer);
    },
    replaceCharacter(str: string) {
      return str.replace('ae', 'ä').replace('oe', 'ö').replace('ue', 'ü');
    },

    navInlineFunction() {
      // get link if nav inline property is true
      const { market, lang } = this.$i18n.localeProperties;

      this.Storyblok.get(`cdn/stories`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
        by_slugs: `${market}-${lang}/*${this.category}/*`,
        filter_query: {
          navInline: {
            is: true,
          },
          navInlineHideSelf: {
            is: false,
          },
        },
      }).then((pageQueryResult: any) => {
        const pageQueryData = pageQueryResult.data.stories;
        this.navData = pageQueryData.map(function (dataItem: any) {
          return {
            link: transformLink({
              url: dataItem.full_slug,
              label: dataItem.name,
              showRed: dataItem.content.navInlineRed,
            }),
          };
        });
      });

      // get category
      this.Storyblok.get(`cdn/stories`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
        by_slugs: `${market}-${lang}/*${this.category}/*`,
        filter_query: {
          categoryLabel: {
            is: 'not_empty',
          },
        },
      }).then((categoryQueryResult: any) => {
        const pageQueryData = categoryQueryResult.data.stories;
        this.categoryData = pageQueryData.map(function (dataItem: any) {
          return {
            categoryLabel: dataItem.content.categoryLabel,
          };
        });

        this.$nextTick(() => {
          if (this.categoryData.length && this.categoryData[0].categoryLabel != null) {
            this.getService = this.categoryData[0].categoryLabel;
          }
        });
      });

      // show logo animation if moveToLogo property is true
      this.Storyblok.get(`cdn/stories`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
        by_slugs: `${market}-${lang}/*${this.category}/*`,
        filter_query: {
          moveToLogo: {
            is: true,
          },
        },
      }).then((animationQueryResult: any) => {
        const pageQueryData = animationQueryResult.data.stories;
        this.animationData = pageQueryData.map(function (dataItem: any) {
          return {
            moveToLogo: dataItem.content.moveToLogo,
          };
        });
        this.$nextTick(() => {
          if (this.animationData.length && this.animationData[0].moveToLogo === true) {
            this.moveToLogo = true;
            this.$emit('moveToLogo');
          }
        });
      });

      this.Storyblok.get(`cdn/stories`, {
        version: process.env.STORYBLOK_CONTENT_VERSION,
        resolve_links: 'url',
        by_slugs: `${market}-${lang}/*${this.category}/*`,
        filter_query: {
          serviceButton: {
            is: true,
          },
        },
      }).then((contactQueryResult: any) => {
        const pageQueryData = contactQueryResult.data.stories;
        this.serviceData = pageQueryData.map(function (dataItem: any) {
          return {
            serviceButton: dataItem.content.serviceButton,
            button: dataItem.content.buttonLabel,
            formLayer: {
              moduleHeader: {
                headline: {
                  text: dataItem.content.formLayer[0].headlineText,
                  level: 'h2',
                  fontStyle: 'h3',
                },
                copy: dataItem.content.formLayer[0].copy,
              },
              id: dataItem.content.formLayer[0].id,
              contactAddress: dataItem.content.formLayer[0].contactAddress,
              buttonLabel: dataItem.content.formLayer[0].buttonLabel,
              marginal: transformRichText(dataItem.content.formLayer[0].marginal),
              contactForm: transformRichText(dataItem.content.formLayer[0].contactForm),
              confirmation: transformRichText(dataItem.content.formLayer[0].confirmation),
            },
          };
        });
        this.$nextTick(() => {
          if (this.serviceData[0] && this.serviceData[0].serviceButton === true) {
            this.service = true;
          }
        });
      });
    },
  },
  computed: {
    getCurrentSite() {
      let currentSite = this.current;
      return currentSite;
    },
  },
  mounted() {
    if (this.category) {
      this.navInlineFunction();
    }
    if (this.$route.query.showForm && !this.$route.query.formSent) this.openLayer();
    if (this.$route.query.showForm && this.$route.query.formSent) {
      this.closeLayer();
    }
    if (this.state === true) {
      this.navInline = true;
    }
  },
});
